import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = slug ? categoryMap[slug] : undefined;
  const articles = slug ? getArticlesByCategory(slug) : [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const pageTitle = currentCategory
    ? `${currentCategory.name} - 海角社区`
    : '内容分类 - 海角社区';

  const pageDescription = currentCategory
    ? `${currentCategory.description}。海角社区${currentCategory.name}板块，已有${currentCategory.count.toLocaleString()}篇真实故事。`
    : '海角社区六大内容板块，满足不同情感交流需求。';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.haijiao.cn/' },
      {
        '@type': 'ListItem',
        position: 2,
        name: currentCategory?.name || '分类',
        item: `https://www.haijiao.cn/category/${slug || ''}`,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
              <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium">
                  {currentCategory?.name || '内容分类'}
                </span>
              </nav>
            </div>
          </div>

          {/* Page Header */}
          <div className="bg-background-50 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                {currentCategory?.name || '内容分类'}
              </h1>
              <p className="text-sm text-foreground-600 max-w-lg">
                {currentCategory?.description || '选择你感兴趣的内容板块，发现更多精彩故事'}
              </p>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 py-2 overflow-x-auto flex-nowrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Articles List */}
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
            {articles.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-foreground-500">
                    共 <strong className="text-foreground-800">{articles.length}</strong> 篇文章
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {articles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          title={`${article.title} - 海角社区${currentCategory?.name || ''}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4 md:p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-foreground-400">{article.date}</span>
                        </div>
                        <h2 className="text-sm md:text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h2>
                        <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <img
                              src={article.authorAvatar}
                              alt={article.author}
                              className="w-5 h-5 rounded-full object-cover"
                            />
                            <span className="text-xs text-foreground-500">{article.author}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-foreground-400">
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line"></i>
                              {article.readCount.toLocaleString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-chat-3-line"></i>
                              {article.commentCount}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容</p>
                <Link
                  to="/"
                  className="inline-block mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}